import { useCallback, useContext, useMemo } from "react";

import { itemTotalsSchema } from "~/shared/schemas/commodity-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import type { HandlingUnit } from "~/shared/types/quote-details-types";
import { calculateItemDensity } from "~/shared/utils/commodity-utils.ts";

import { QuoteDetailsContext } from "../context/quote-details-context.tsx";

export type ItemCalculationRequest = {
	commodity: string;
	length: number | null;
	width: number | null;
	height: number | null;
	quantity: number | null;
	weight: number | null;
	stackable: boolean;
}[];

export const fetchCalculatedLinearFeet = async (
	items: ItemCalculationRequest,
) => {
	const response = await postAndValidate(
		apiRoutes.CALCULATEITEMTOTALS,
		items,
		itemTotalsSchema,
	);
	if (!response.success) {
		throw new Error(response.error);
	}
	return response.data.totalLinearFeet;
};

export const useItemCalculation = (
	items: HandlingUnit[],
	measuringType: string,
) => {
	const { onChange } = useContext(QuoteDetailsContext);

	const getItemsWithDensityAndEstClass = useMemo(() => {
		return items.map((item) => {
			const density = calculateItemDensity(item, measuringType);
			const {
				quantity,
				weight,
				length,
				width,
				height,
				description,
				isStackable,
				pieceCount,
			} = item;

			return {
				...item,
				commodity: description,
				quantity,
				weight,
				length,
				width,
				height,
				stackable: isStackable,
				pieceCaseCount: pieceCount,
				density,
			};
		});
	}, [items, measuringType]);

	const calculationSignature = useMemo(() => {
		const dimensions = items.map((item) => [
			item.quantity ?? 0,
			item.weight ?? 0,
			item.length ?? 0,
			item.width ?? 0,
			item.height ?? 0,
			item.isStackable ?? false,
		]);
		return `${measuringType}|${JSON.stringify(dimensions)}`;
	}, [items, measuringType]);

	const getCalculatedItemTotals = useCallback(
		async (signal?: AbortSignal) => {
			const bodyItems = items
				.map((item) => {
					const density = calculateItemDensity(item, measuringType);
					const {
						quantity,
						description,
						isStackable,
						pieceCount,
						weight,
						length,
						width,
						height,
					} = item;

					let convertedWeight = weight;
					if (measuringType === "KG/CM") {
						convertedWeight = Math.ceil(
							(convertedWeight ?? 0) * 2.204_622_621_85,
						);
					}

					return {
						commodity: description,
						quantity,
						weight: convertedWeight ?? 0,
						length,
						width,
						height,
						stackable: isStackable,
						pieceCaseCount: pieceCount,
						density,
					};
				})
				.filter(
					(item) =>
						(item.quantity ?? 0) > 0 &&
						(item.weight ?? 0) > 0 &&
						(item.length ?? 0) > 0 &&
						(item.width ?? 0) > 0 &&
						(item.height ?? 0) > 0,
				)
				.map(
					({
						commodity,
						length,
						width,
						height,
						quantity,
						weight,
						stackable,
					}) => ({
						commodity,
						length,
						width,
						height,
						quantity,
						weight,
						stackable,
					}),
				);

			try {
				if (bodyItems.length === 0) {
					return [];
				}

				const response = await (signal
					? postAndValidate(
							apiRoutes.CALCULATEITEMTOTALS,
							bodyItems,
							itemTotalsSchema,
							{ signal },
						)
					: postAndValidate(
							apiRoutes.CALCULATEITEMTOTALS,
							bodyItems,
							itemTotalsSchema,
						));

				if (signal?.aborted) {
					return bodyItems;
				}
				if (response.success) {
					onChange("totalLinearFeet", response.data.totalLinearFeet);
				}
				return bodyItems;
			} catch (error) {
				if (signal?.aborted) {
					return bodyItems;
				}
				console.error("failed to calculate item totals", error);
				return error;
			}
		},
		[items, measuringType, onChange],
	);

	return {
		getItemsWithDensityAndEstClass,
		getCalculatedItemTotals,
		fetchCalculatedLinearFeet,
		calculationSignature,
	};
};
